import * as i0 from '@angular/core';
import { EventEmitter, PLATFORM_ID, Component, ChangeDetectionStrategy, Inject, Input, Output, ViewChild, NgModule } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';

var YoutubeState;
(function (YoutubeState) {
    YoutubeState[YoutubeState["Unstarted"] = -1] = "Unstarted";
    YoutubeState[YoutubeState["Ended"] = 0] = "Ended";
    YoutubeState[YoutubeState["Playing"] = 1] = "Playing";
    YoutubeState[YoutubeState["Paused"] = 2] = "Paused";
    YoutubeState[YoutubeState["Buffering"] = 3] = "Buffering";
    YoutubeState[YoutubeState["Cued"] = 5] = "Cued";
})(YoutubeState || (YoutubeState = {}));

class YoutubeUtility {
    static VideoIdFromUrl(url) {
        const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
        const match = url.match(regExp);
        return match && match[7].length === 11 ? match[7] : url;
    }
}

class YoutubePlayerComponent {
    get host() {
        console.log(this.cookies);
        return !this.cookies ? 'https://www.youtube-nocookie.com' : null;
    }
    constructor(platformId) {
        this.platformId = platformId;
        this.ready = new EventEmitter();
        this.play = new EventEmitter();
        this.end = new EventEmitter();
        this.pause = new EventEmitter();
        this.change = new EventEmitter();
        this.error = new EventEmitter();
        this.stateClassName = 'is--unloaded';
    }
    async ngOnInit() {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        const YT = await this.apiLoaded();
        this.player = new YT.Player(this.ref.nativeElement, {
            videoId: YoutubeUtility.VideoIdFromUrl(this.src),
            host: this.host,
            playerVars: this.config,
            events: {
                onReady: (e) => this.ready.emit(e),
                onError: (e) => this.error.emit(e),
                onStateChange: (e) => {
                    const stateName = YoutubeState[e.data].toLowerCase();
                    this.stateClassName = `is--${stateName}`;
                    if (e.data === YoutubeState.Playing) {
                        this.play.emit(e);
                    }
                    if (e.data === YoutubeState.Paused) {
                        this.pause.emit(e);
                    }
                    if (e.data === YoutubeState.Ended) {
                        this.end.emit(e);
                    }
                    this.change.emit(e);
                },
            },
        });
    }
    apiLoaded() {
        return new Promise((resolve, reject) => {
            const tag = document.createElement('script');
            tag.id = 'youtube-component-api';
            tag.src = 'https://www.youtube.com/iframe_api';
            const exist = document.getElementById(tag.id);
            if (!exist) {
                const firstScriptTag = document.getElementsByTagName('script')[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
            }
            let timeout = 0;
            const maxTimeout = 30000;
            const interval = 100;
            const timer = setInterval(() => {
                const YT = window.YT;
                timeout += interval;
                if (timeout >= maxTimeout) {
                    clearInterval(timer);
                    reject(new Error('Youtube Api not loaded'));
                }
                else if (YT && YT.Player) {
                    clearInterval(timer);
                    resolve(YT);
                }
            }, interval);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.2", ngImport: i0, type: YoutubePlayerComponent, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.2", type: YoutubePlayerComponent, selector: "fstr-youtube-player", inputs: { src: "src", config: "config", cookies: "cookies" }, outputs: { ready: "ready", play: "play", end: "end", pause: "pause", change: "change", error: "error" }, viewQueries: [{ propertyName: "ref", first: true, predicate: ["ref"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"fstr-youtube__container {{ stateClassName }}\">\n  <div class=\"fstr-youtube__container__iframe\" #ref></div>\n</div>\n", styles: [".fstr-youtube__container{width:100%;height:0;overflow:hidden;padding-bottom:56.25%;position:relative;display:block}::ng-deep .fstr-youtube__container__iframe{width:100%;height:100%;position:absolute;inset:0}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.2", ngImport: i0, type: YoutubePlayerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fstr-youtube-player', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"fstr-youtube__container {{ stateClassName }}\">\n  <div class=\"fstr-youtube__container__iframe\" #ref></div>\n</div>\n", styles: [".fstr-youtube__container{width:100%;height:0;overflow:hidden;padding-bottom:56.25%;position:relative;display:block}::ng-deep .fstr-youtube__container__iframe{width:100%;height:100%;position:absolute;inset:0}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.InjectionToken, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; }, propDecorators: { src: [{
                type: Input
            }], config: [{
                type: Input
            }], cookies: [{
                type: Input
            }], ready: [{
                type: Output
            }], play: [{
                type: Output
            }], end: [{
                type: Output
            }], pause: [{
                type: Output
            }], change: [{
                type: Output
            }], error: [{
                type: Output
            }], ref: [{
                type: ViewChild,
                args: ['ref', { static: true }]
            }] } });

class YoutubePlayerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.2", ngImport: i0, type: YoutubePlayerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.2", ngImport: i0, type: YoutubePlayerModule, declarations: [YoutubePlayerComponent], imports: [CommonModule], exports: [YoutubePlayerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.2", ngImport: i0, type: YoutubePlayerModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.2", ngImport: i0, type: YoutubePlayerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [YoutubePlayerComponent],
                    imports: [CommonModule],
                    exports: [YoutubePlayerComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { YoutubePlayerComponent, YoutubePlayerModule, YoutubeState, YoutubeUtility };
